import { policyResultKeys, type PolicyPlacement } from './policy-order-api'
import { decodePolicyMutation, decodePolicyRefreshStatus, type PolicyRefreshStatus } from '../lib/policy-authoring'
import type { components } from '@/lib/openapi.generated'
import {
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requireBoolean,
  requireNumber,
  requireString,
  requireTimestamp,
  withQuery,
} from '@/lib/api'

type Schemas = components['schemas']
export const assistantPolicyKeys = {
  result: (entityId: string) => policyResultKeys.type(entityId, ASSISTANT_POLICY_TYPE),
}
export const ASSISTANT_POLICY_TYPE = 'entity:ai.assistant.enabled'
export type AssistantPolicy = Omit<Schemas['AssistantPolicyResponse'], 'selectorGui'> & { selectorGui?: Record<string, unknown> | null }
export type AssistantPolicyEdit = Omit<AssistantPolicy, 'id' | 'revision' | 'priority'> & { placement?: PolicyPlacement }
export type { PolicyRefreshStatus }
export type AssistantPolicyResult = Omit<
  Schemas['AssistantPolicyResultResponse'],
  'selectionStatus' | 'refreshStatus'
> & {
  selectionStatus: 'NeverEvaluated' | 'Evaluated'
  refreshStatus: PolicyRefreshStatus
}

function decodePolicy(raw: unknown): AssistantPolicy {
  if (!isRecord(raw)) throw new Error('Malformed assistant policy')
  return {
    id: requireString(raw.id, 'id'),
    name: requireString(raw.name, 'name'),
    value: requireBoolean(raw.value, 'value'),
    selectorExpression: nullableString(raw.selectorExpression, 'selectorExpression'),
    selectorGui: decodeSelectorGui(raw.selectorGui),
    priority: requireNumber(raw.priority, 'priority'),
    revision: requireNumber(raw.revision, 'revision'),
  } satisfies AssistantPolicy
}

function decodeSelectorGui(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null
  if (!isRecord(raw)) throw new Error('Malformed policy selector UI state')
  return raw
}

const decodeMutation = (raw: unknown) =>
  decodePolicyMutation(raw) satisfies Schemas['AssistantPolicyMutationResponse']

function decodeResult(raw: unknown): AssistantPolicyResult {
  if (!isRecord(raw)) throw new Error('Malformed assistant policy result')
  if (raw.selectionStatus !== 'NeverEvaluated' && raw.selectionStatus !== 'Evaluated')
    throw new Error('Unexpected policy selection status')
  return {
    selectionStatus: raw.selectionStatus,
    selectionRevision: nullableNumber(raw.selectionRevision, 'selectionRevision'),
    evaluatedAt: raw.evaluatedAt == null ? null : requireTimestamp(raw.evaluatedAt, 'evaluatedAt'),
    refreshStatus: decodePolicyRefreshStatus(raw.refreshStatus),
    failureCode: nullableString(raw.failureCode, 'failureCode'),
    value: raw.value == null ? null : requireBoolean(raw.value, 'value'),
    isDefault: requireBoolean(raw.isDefault, 'isDefault'),
  } satisfies AssistantPolicyResult
}

const policiesPath = (entityId: string) => `/api/v1/entities/${entityId}/assistant-policies`
const policyPath = (entityId: string, policyId: string) => `${policiesPath(entityId)}/${policyId}`

export const assistantPolicyApi = {
  get: (entityId: string, policyId: string) =>
    request(policyPath(entityId, policyId), decodePolicy),
  result: (entityId: string) =>
    request(`/api/v1/entities/${entityId}/assistant-policy`, decodeResult),
  create: (entityId: string, edit: AssistantPolicyEdit, operationId: string) =>
    request(
      policiesPath(entityId),
      decodeMutation,
      json('POST', {
        ...edit,
        operationId,
        placement: edit.placement ? { ...edit.placement, atStart: false } : undefined,
      } satisfies Schemas['CreateAssistantPolicyRequest']),
    ),
  update: (entityId: string, policy: AssistantPolicy, edit: AssistantPolicyEdit) =>
    request(
      policyPath(entityId, policy.id),
      decodeMutation,
      json('PUT', {
        ...edit,
        expectedRevision: policy.revision,
      } satisfies Schemas['UpdateAssistantPolicyRequest']),
    ),
  delete: (entityId: string, policy: Pick<AssistantPolicy, 'id' | 'revision'>) =>
    request(
      withQuery(policyPath(entityId, policy.id), {
        expectedRevision: String(policy.revision),
      }),
      decodeMutation,
      { method: 'DELETE' },
    ),
}
